import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Card, Spinner, Table } from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { useDashboardStore } from "@/stores/dashboard";

import {
  MetricImprovement,
  TrendsUnit,
  useTeamTrends,
} from "./hooks/useTeamTrends";

type TrendsRow = {
  metric: string;
  improvement: MetricImprovement;
  seasonAvg: string;
  last30Days: string;
  last30DaysVsAvg: string;
  trendColor: string;
};

type TeamTrendsProps = {
  teamId: string;
};

export const TeamTrends = ({ teamId }: TeamTrendsProps) => {
  const { filter, includePractice, selectedYear } = useDashboardStore();
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const gameType = includePractice
    ? filter !== "hitting"
      ? "bullpen"
      : "batting_practice"
    : "game";

  const {
    hitting,
    pitching,
    isLoadingTeamSeasonTrends,
    isLoadingTeamLast30DaysDateTrends,
    teamSeasonTrendsError,
    teamLast30DaysDateTrendsError,
  } = useTeamTrends(teamId, filter, gameType, selectedYear);

  const rawData = filter === "hitting" ? hitting : pitching;
  const isLoading =
    isLoadingTeamSeasonTrends || isLoadingTeamLast30DaysDateTrends;

  const columns = useMemo(
    (): ColumnDef<TrendsRow>[] => [
      {
        header: "Metric",
        accessorKey: "metric",
        cell: ({ row }) => (
          <span className="font-medium text-text-light">
            {row.original.metric}
          </span>
        ),
      },
      {
        header: "Season Avg.",
        accessorKey: "seasonAvg",
      },
      {
        header: "Last 30 Days",
        accessorKey: "last30Days",
        cell: ({ row }) => (
          <span className={row.original.trendColor}>
            {row.original.last30Days}
          </span>
        ),
      },
      {
        header: "Last 30 Days vs Avg.",
        accessorKey: "last30DaysVsAvg",
        cell: ({ row }) => (
          <span className={row.original.trendColor}>
            {row.original.last30DaysVsAvg}
          </span>
        ),
      },
    ],
    [],
  );

  const tableData = useMemo((): TrendsRow[] => {
    if (!rawData) return [];
    return rawData.map((row) => ({
      metric: row.metric,
      improvement: row.improvement,
      seasonAvg: getFixedOrDash(row.seasonAvg, row.unit),
      last30Days: getFixedOrDash(row.last30Days, row.unit),
      last30DaysVsAvg: getDiffOrDash(row.last30DaysVsAvg, row.unit),
      trendColor: getTrendColor(
        row.improvement,
        row.seasonAvg,
        row.last30Days,
        row.unit,
      ),
    }));
  }, [rawData]);

  const renderContent = () => {
    if (teamSeasonTrendsError && teamLast30DaysDateTrendsError) {
      return (
        <div className="flex items-center justify-center p-10">
          <p className="text-sm text-accent-dark-3">
            Error loading team trends data
          </p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      );
    }

    if (!rawData || rawData.length === 0) {
      return (
        <div className="flex items-center justify-center p-10">
          <p className="text-sm text-accent-dark-3">No data available</p>
        </div>
      );
    }

    return (
      <Table
        size="compact"
        data={tableData}
        columns={columns}
        total={tableData.length}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        headerClassName="text-xs font-medium text-accent-dark-3 uppercase"
        cellClassName="text-sm text-text-light"
      />
    );
  };

  return (
    <div className="relative flex flex-col">
      <h2 className="text-lg font-bold text-text-light mb-4">Team Trends</h2>
      <Card className="bg-white light:bg-white rounded-md p-4">
        {renderContent()}
      </Card>
    </div>
  );
};

const getFixedOrDash = (value?: number | null, unit?: string) => {
  if (value === null || value === undefined) {
    return "-";
  }
  const valueFixed = unit === "%" ? (value * 100).toFixed(2) : value.toFixed(2);
  return unit ? `${valueFixed} ${unit}` : valueFixed;
};

const getDiffOrDash = (diff?: number | null, unit?: string) =>
  diff === null || diff === undefined
    ? "-"
    : `${diff > 0 ? "+" : ""}${getFixedOrDash(diff, unit)}`;

const getTrendColor = (
  improvement: MetricImprovement,
  seasonAvg?: number | null,
  last30Days?: number | null,
  unit?: TrendsUnit,
) => {
  if (
    seasonAvg === null ||
    seasonAvg === undefined ||
    last30Days === null ||
    last30Days === undefined
  ) {
    return "text-accent-dark-3";
  }
  const seasonAvgFixed =
    unit == "%" ? +(seasonAvg * 100).toFixed(2) : +seasonAvg.toFixed(2);
  const last30DaysFixed =
    unit == "%" ? +(last30Days * 100).toFixed(2) : +last30Days.toFixed(2);
  if (last30DaysFixed === seasonAvgFixed) {
    return "text-accent-dark-3";
  }
  const isTrendColorGreen =
    improvement === "higher"
      ? last30DaysFixed > seasonAvgFixed
      : last30DaysFixed < seasonAvgFixed;
  return isTrendColorGreen ? "text-green" : "text-error";
};
